import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import SnackbarMessage from "../components/common/SnackbarMessage";

const useAdd = (
  queryKey,
  token = null,
  customSuccessMessage = "Data added successfully",
  customErrorMessage = "Failed to add data",
) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [responseData, setResponseData] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    title: "",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const addMutation = useMutation({
    mutationFn: async (data) => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Token ${token.trim()}` } : {};
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/${queryKey}/`,
          data,
          { headers },
        );
        setResponseData(response.data);
        return response.data;
      } catch (error) {
        const errorMessage = error.response?.data?.detail || error.message;
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      setLoading(false);
      setSnackbar({
        open: true,
        message: customSuccessMessage,
        severity: "success",
        title: "Success",
      });
      setResetForm(true);
      setTimeout(() => {
        setResetForm(false);
      }, 100);
      queryClient.invalidateQueries(queryKey);
    },
    onError: (error) => {
      setLoading(false);
      const backendErrors = error.response?.data;
      if (backendErrors && typeof backendErrors === "object") {
        const errorMessages = Object.entries(backendErrors)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`,
          )
          .join("\n");

        setSnackbar({
          open: true,
          message: errorMessages,
          severity: "error",
          title: "Error",
        });
      } else {
        setSnackbar({
          open: true,
          message: customErrorMessage || `Error: ${error.message}`,
          severity: "error",
          title: "Error",
        });
      }
    },
  });

  const handleAdd = (data) => {
    addMutation.mutate(data);
  };

  const isLoading = addMutation.isLoading || loading;

  return {
    handleAdd,
    resetForm,
    loading: isLoading,
    responseData,
    isError: addMutation.isError,
    isSuccess: addMutation.isSuccess,
    SnackbarMessage: (
      <SnackbarMessage
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
        title={snackbar.title}
      />
    ),
  };
};

export default useAdd;
